import React, { useMemo } from 'react';
import CollapsibleFilterPanel, { FilterField } from './CollapsibleFilterPanel';

const PAIR_TYPE_OPTIONS = [
  { value: 'all', label: 'All pair types' },
  { value: 'mixed', label: 'Mixed' },
  { value: 'levels', label: 'Levels' },
];

const DATE_RANGE_OPTIONS = [
  { value: 'season', label: 'This season' },
  { value: 'last_30_days', label: 'Last 30 days' },
  { value: 'last_10', label: 'Last 10 games' },
  { value: 'all_time', label: 'All time' },
];

const controlClass = 'form-input w-full min-w-0 text-sm py-1.5 sm:py-2';

export default function ClubAnalysisPlayerFilters({
  pairTypeFilter,
  onPairTypeFilterChange,
  gameTypeFilter,
  onGameTypeFilterChange,
  gameTypeOptions = [],
  sessionFilter,
  onSessionFilterChange,
  sessionOptions = [],
  dateRangeFilter,
  onDateRangeFilterChange,
}) {
  const pairLabel = PAIR_TYPE_OPTIONS.find((o) => o.value === pairTypeFilter)?.label ?? 'All';
  const dateLabel = DATE_RANGE_OPTIONS.find((o) => o.value === dateRangeFilter)?.label ?? 'This season';
  const gameLabel =
    gameTypeFilter === 'all'
      ? 'All game types'
      : gameTypeOptions.find((g) => g === gameTypeFilter) || gameTypeFilter;
  const sessionLabel =
    sessionFilter === 'all'
      ? 'All sessions'
      : sessionOptions.find((s) => s.id === sessionFilter)?.name || 'Session';

  const hasActiveFilters =
    pairTypeFilter !== 'all' ||
    gameTypeFilter !== 'all' ||
    sessionFilter !== 'all' ||
    dateRangeFilter !== 'season';

  const summary = useMemo(
    () => [pairLabel, gameLabel, sessionLabel, dateLabel].join(' · '),
    [pairLabel, gameLabel, sessionLabel, dateLabel]
  );

  return (
    <CollapsibleFilterPanel summary={summary} hasActiveFilters={hasActiveFilters} ariaLabel="Player analysis filters">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4 sm:gap-4">
        <FilterField id="club-analysis-pair-type" label="Pair type" labelShort="Pair">
          <select
            id="club-analysis-pair-type"
            value={pairTypeFilter}
            onChange={(e) => onPairTypeFilterChange(e.target.value)}
            className={controlClass}
          >
            {PAIR_TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </FilterField>

        <FilterField id="club-analysis-game-type" label="Game type" labelShort="Game">
          <select
            id="club-analysis-game-type"
            value={gameTypeFilter}
            onChange={(e) => onGameTypeFilterChange(e.target.value)}
            className={controlClass}
          >
            <option value="all">All game types</option>
            {gameTypeOptions.map((gt) => (
              <option key={gt} value={gt}>
                {gt}
              </option>
            ))}
          </select>
        </FilterField>

        <FilterField id="club-analysis-session" label="Session" labelShort="Session">
          <select
            id="club-analysis-session"
            value={sessionFilter}
            onChange={(e) => onSessionFilterChange(e.target.value)}
            className={controlClass}
          >
            <option value="all">All sessions</option>
            {sessionOptions.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name} {s.displayDate ? `(${s.displayDate})` : ''}
              </option>
            ))}
          </select>
        </FilterField>

        <FilterField id="club-analysis-date-range" label="Date range" labelShort="Dates">
          <select
            id="club-analysis-date-range"
            value={dateRangeFilter}
            onChange={(e) => onDateRangeFilterChange(e.target.value)}
            className={controlClass}
          >
            {DATE_RANGE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </FilterField>
      </div>
    </CollapsibleFilterPanel>
  );
}
